import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Filter, X, Search, Calendar, Check } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Header } from "./WorkspacesTab";

const ACTION_LABEL = {
  "totp.enabled": "2FA ingeschakeld",
  "totp.login": "2FA login",
  "totp.reset": "2FA gereset",
  "impersonate.start": "God Mode gestart",
  "impersonate.exit": "God Mode beëindigd",
  "impersonate.access": "God Mode actie",
  "config.update": "Config opgeslagen",
  "faq.create": "FAQ toegevoegd",
  "faq.update": "FAQ aangepast",
  "faq.delete": "FAQ verwijderd",
  "workspace.suspend": "Workspace op pauze",
  "workspace.unsuspend": "Workspace heractiveerd",
  "workspace.delete": "Workspace verwijderd",
  "email_template.update": "Email template aangepast",
  "email_template.reset": "Email template gereset",
  "audit.export": "Audit log geëxporteerd",
};

export default function AuditLogsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [brands, setBrands] = useState([]);
  const [selected, setSelected] = useState([]); // brand ids
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [exporting, setExporting] = useState(false);

  const buildParams = () => {
    const p = {};
    if (selected.length) p.brand_ids = selected.join(",");
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    return p;
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/super-admin/audit", { params: buildParams() });
      setRows(data);
    } catch { toast.error("Kon audit-log niet laden"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/super-admin/workspaces");
        setBrands(data.map((b) => ({ id: b.id, name: b.name })));
      } catch { /* ignore */ }
    })();
  }, []);

  useEffect(() => { refresh(); }, [selected, dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "medium" }); }
    catch { return iso; }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams(buildParams()).toString();
      const url = `${api.defaults.baseURL}/super-admin/audit/export${params ? `?${params}` : ""}`;
      const bh = localStorage.getItem("bh_token");
      const sa = localStorage.getItem("bh_sa_token");
      const r = await fetch(url, { headers: { Authorization: `Bearer ${bh}`, "X-Super-Admin-Token": sa } });
      if (!r.ok) throw new Error("Export mislukt");
      const blob = await r.blob();
      const dl = document.createElement("a");
      dl.href = URL.createObjectURL(blob);
      dl.download = `brandhub-audit-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(dl); dl.click(); dl.remove();
      toast.success("CSV gedownload");
    } catch (e) { toast.error(e?.message || "Export mislukt"); }
    finally { setExporting(false); }
  };

  const clearFilters = () => { setSelected([]); setDateFrom(""); setDateTo(""); };
  const hasFilters = selected.length || dateFrom || dateTo;

  return (
    <div data-testid="sa-audit">
      <Header title="Audit Logs" sub="Elke super-admin-actie wordt vastgelegd. Filter op workspace + datumbereik en exporteer voor compliance." />

      <div className="rounded-[var(--global-radius-xl)] border border-gray-200 bg-white p-4 mb-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <div className="text-[11px] uppercase tracking-widest text-gray-400 mb-1 flex items-center gap-1.5">
            <Filter className="w-3 h-3" strokeWidth={1.75} /> Workspaces
          </div>
          <BrandCombobox brands={brands} selected={selected} onChange={setSelected} />
        </div>
        <div className="w-[160px]">
          <div className="text-[11px] uppercase tracking-widest text-gray-400 mb-1 flex items-center gap-1.5">
            <Calendar className="w-3 h-3" strokeWidth={1.75} /> Van
          </div>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="bg-white h-10" data-testid="sa-audit-date-from" />
        </div>
        <div className="w-[160px]">
          <div className="text-[11px] uppercase tracking-widest text-gray-400 mb-1 flex items-center gap-1.5">
            <Calendar className="w-3 h-3" strokeWidth={1.75} /> Tot
          </div>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="bg-white h-10" data-testid="sa-audit-date-to" />
        </div>
        <div className="flex items-center gap-2">
          {hasFilters ? (
            <button onClick={clearFilters} data-testid="sa-audit-clear" className="h-10 px-3 rounded-[var(--global-radius)] border border-gray-200 bg-white text-xs text-gray-600 hover:text-[#1a1a1a] hover:border-gray-300">
              Reset filters
            </button>
          ) : null}
          <Button
            onClick={exportCsv}
            disabled={exporting}
            data-testid="sa-audit-export"
            className="h-10 rounded-[var(--global-radius)] bg-[var(--global-action)] text-white hover:opacity-90 disabled:opacity-50"
          >
            <Download className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} />
            {exporting ? "Bezig…" : `Exporteer CSV (${rows.length})`}
          </Button>
        </div>
      </div>

      <div className="rounded-[var(--global-radius-xl)] border border-gray-200 bg-white overflow-hidden">
        <div className="grid grid-cols-[180px_200px_1fr_200px] gap-4 px-5 py-3 border-b border-gray-100 text-[11px] uppercase tracking-widest text-gray-500">
          <div>Timestamp</div>
          <div>Admin</div>
          <div>Actie</div>
          <div>Workspace</div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-gray-500">Laden…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">Geen events voor deze filter.</div>
        ) : (
          rows.map((r) => (
            <div key={r.id} data-testid={`sa-audit-row-${r.id}`} className="grid grid-cols-[180px_200px_1fr_200px] gap-4 px-5 py-3 border-b border-gray-50 last:border-0 items-center text-sm">
              <div className="font-mono text-xs text-gray-500">{fmt(r.timestamp)}</div>
              <div className="text-gray-700 truncate">{r.admin_email}</div>
              <div>
                <div className="text-[#1a1a1a]">{ACTION_LABEL[r.action] || r.action}</div>
                {r.details && Object.keys(r.details).length > 0 && (
                  <div className="text-[11px] text-gray-400 font-mono truncate">{JSON.stringify(r.details)}</div>
                )}
              </div>
              <div className="text-gray-600 truncate">{r.workspace_name || <span className="text-gray-300">—</span>}</div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// Searchable multi-select combobox for workspace filtering.
// Selected brands render as removable chips inside the input shell; typing
// opens a popover with matched brands. Click a result to add it.
function BrandCombobox({ brands, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef();
  const inputRef = useRef();

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const selectedBrands = brands.filter((b) => selected.includes(b.id));
  const q = query.trim().toLowerCase();
  const matches = brands.filter((b) =>
    !selected.includes(b.id) && (!q || b.name.toLowerCase().includes(q))
  );

  const toggle = (id) => {
    if (selected.includes(id)) onChange(selected.filter((x) => x !== id));
    else onChange([...selected, id]);
    setQuery("");
    inputRef.current?.focus();
  };

  return (
    <div ref={wrapRef} className="relative" data-testid="sa-audit-brand-combobox">
      <div
        className="flex items-center flex-wrap gap-1 min-h-10 bg-white border border-gray-200 rounded-[var(--global-radius)] px-2 py-1 cursor-text"
        onClick={() => { setOpen(true); inputRef.current?.focus(); }}
      >
        {selectedBrands.length === 0 && (
          <Search className="w-3.5 h-3.5 text-gray-400 ml-1" strokeWidth={1.75} />
        )}
        {selectedBrands.map((b) => (
          <span key={b.id} data-testid={`sa-audit-chip-${b.id}`} className="inline-flex items-center gap-1 h-7 pl-2 pr-1 rounded-[var(--global-radius)] bg-[#1a1a1a] text-white text-xs font-medium">
            {b.name}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); toggle(b.id); }}
              className="w-5 h-5 inline-flex items-center justify-center rounded-sm hover:bg-white/15"
            >
              <X className="w-3 h-3" strokeWidth={2.5} />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={selectedBrands.length ? "" : "Zoek merken…"}
          data-testid="sa-audit-brand-search"
          className="flex-1 min-w-[80px] h-7 bg-transparent border-0 outline-none text-sm placeholder:text-gray-400"
          style={{ boxShadow: "none" }}
        />
      </div>
      {open && matches.length > 0 && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-[var(--global-radius-lg)] shadow-lg z-20 max-h-60 overflow-y-auto p-1" data-testid="sa-audit-brand-dropdown">
          {matches.map((b) => (
            <button
              key={b.id}
              type="button"
              onClick={() => toggle(b.id)}
              data-testid={`sa-audit-option-${b.id}`}
              className="w-full flex items-center justify-between px-3 h-8 rounded-[var(--global-radius)] text-sm text-[#1a1a1a] hover:bg-gray-100"
            >
              <span>{b.name}</span>
              <Check className="w-3.5 h-3.5 text-gray-300" strokeWidth={1.75} />
            </button>
          ))}
        </div>
      )}
      {open && query && matches.length === 0 && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-[var(--global-radius-lg)] shadow-lg z-20 px-3 py-2 text-xs text-gray-500">
          Geen matches voor &quot;{query}&quot;
        </div>
      )}
    </div>
  );
}
